// Shared list filters of the follow-tax tabs: date range, car type and search text.
// The parent (Index.vue) owns the values; each tab copies them into its own `filter`
// object (snake_case keys, as read by /process/get_process and /process/get_pending_process)
// on creation and whenever they change.
import CursorPager from './CursorPager';

// most rows "select all" takes (the notice above the table says so when it stops there)
export const SELECT_ALL_MAX = 2000;

export default {
  components: { CursorPager },
  props: {
    startDate: { type: String, default: '' },
    endDate: { type: String, default: '' },
    date_type: { type: Number, default: 0 },
    carTypeId: { type: Number, default: 0 },
    // search text already applied by the parent (plate or customer name)
    search: { type: String, default: '' },
    // false while the tab is not the visible one, so hidden tabs don't refetch
    active: { type: Boolean, default: true },
  },
  data: () => ({
    // previous / next paging: cursorHistory[i] is the cursor that loads page i + 1
    hasMore: false,
    nextCursor: null,
    cursorHistory: [null],
    // endpoint of this tab's list (the pending tab has its own)
    listEndpoint: 'get_process',
  }),
  created() {
    this.applyListFilter();
    this.filter.cursor = null; // first page
  },
  methods: {
    applyListFilter() {
      this.filter.start_date = this.startDate;
      this.filter.end_date = this.endDate;
      this.filter.date_type = this.date_type;
      this.filter.car_type_filter = this.carTypeId;
      this.filter.start_with = this.search;
      // this page searches plate / customer name only, never phone
      this.filter.search_phone = false;
      // page by cursor (no total count), see backend _follow_paging
      this.filter.cursor_mode = true;
    },
    applyPageResult(result) {
      this.hasMore = !!result.has_more;
      this.nextCursor = result.next_cursor || null;
    },
    resetPaging() {
      this.filter.page = 1;
      this.filter.cursor = null;
      this.cursorHistory = [null];
    },
    // Every row that matches the current filters (date range, date type, car type, search, round),
    // whatever page it is on: the same query as the list, read page by page with the cursor.
    async fetchAllMatching(max = SELECT_ALL_MAX) {
      const filter = { ...this.filter, cursor: null, item_per_page: 200, cursor_mode: true };
      const rows = [];
      let truncated = false;
      for (;;) {
        const res = await this.$axios.post(`${this.$baseUrl}/process/${this.listEndpoint}`, {
          token: this.$jwt.sign(filter, this.$privateKey, { noTimestamp: true }),
        });
        rows.push(...res.result.data);
        if (!res.result.has_more) break;
        if (rows.length >= max) {
          truncated = true;
          break;
        }
        filter.cursor = res.result.next_cursor;
      }
      return { rows: rows.slice(0, max), truncated };
    },
    // a tab can adjust a row it did not get from its own list (InProgress: the fee)
    normalizeSelectedRow(row) {
      return row;
    },
    async selectAllMatching() {
      const { rows, truncated } = await this.fetchAllMatching();
      // rows on the current page keep their own objects, so inline edits reach the selection
      const onPage = new Map(this.list.map((item) => [item._id, item]));
      this.selected = rows.map((row) => onPage.get(row._id) || this.normalizeSelectedRow(row));
      this.$emit('on-selected', this.selected);
      return { count: this.selected.length, truncated };
    },
    goNextPage() {
      if (!this.hasMore || this.loading) return;
      this.cursorHistory.push(this.nextCursor);
      this.filter.page += 1;
      this.filter.cursor = this.nextCursor;
      this.getData();
    },
    goPreviousPage() {
      if (this.cursorHistory.length <= 1 || this.loading) return;
      this.cursorHistory.pop();
      this.filter.page -= 1;
      this.filter.cursor = this.cursorHistory[this.cursorHistory.length - 1];
      this.getData();
    },
    changePageSize(size) {
      this.filter.item_per_page = size;
      this.resetPaging();
      this.getData();
    },
    // Several inputs can change in the same tick (e.g. choosing a round also
    // adjusts the dates), so coalesce them into a single request.
    reloadForListFilter() {
      if (!this.active) return;
      clearTimeout(this.listReloadTimer);
      this.listReloadTimer = setTimeout(() => {
        if (!this.active) return;
        this.applyListFilter();
        this.resetPaging();
        this.getData();
      }, 60);
    },
  },
  beforeDestroy() {
    clearTimeout(this.listReloadTimer);
  },
  watch: {
    startDate: 'reloadForListFilter',
    endDate: 'reloadForListFilter',
    date_type: 'reloadForListFilter',
    carTypeId: 'reloadForListFilter',
    search: 'reloadForListFilter',
  },
};
